import SeoMeta from 'components/Basic/Head/SeoMeta';
import Registration from 'components/Pages/Registration';
import { ConfirmAccount } from 'components/Pages/Registration/ConfirmAccount';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { GdprActionTypeEnumApi, GdprConsentsQueryDocumentApi, GdprConsentsQueryVariablesApi } from 'graphql/generated';
import checkIsLoggedOnSSR from 'helpers/checkIsLoggedOnSSR';
import { createClient } from 'helpers/createClient';
import { initServerSideProps, ServerSidePropsType } from 'helpers/InitServerSideProps';
import { getServerSidePropsWrapper } from 'helpers/serverSide/getServerSidePropsWrapper';
import { useGtmStaticPageView } from 'hooks/gtm/useGtmStaticPageView';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { NextPageExtended } from 'types/global';
import { getInternationalizedStaticUrls } from 'utils/getInternationalizedStaticUrls';
import { useGtmStaticPageViewEvent } from 'utils/Gtm/EventFactories';

const Index: NextPageExtended<ServerSidePropsType> = () => {
    const { t } = useTranslation();
    const { url: domainUrl } = useDomainConfig();
    const [registrationUrl] = getInternationalizedStaticUrls(['/registration'], domainUrl);
    const breadcrumbs = useMemo(() => [{ name: t('Registration'), slug: registrationUrl }], [registrationUrl, t]);
    const gtmStaticPageViewEvent = useGtmStaticPageViewEvent('other', breadcrumbs);
    useGtmStaticPageView(gtmStaticPageViewEvent);
    const router = useRouter();

    const confirmOnlyValue = router.query.confirmOnly;

    return (
        <>
            <SeoMeta title={t('Registration')} />
            {confirmOnlyValue ? (
                <ConfirmAccount breadcrumbs={breadcrumbs} />
            ) : (
                <Registration breadcrumbs={breadcrumbs} />
            )}
        </>
    );
};

export const getServerSideProps = getServerSidePropsWrapper(
    ({ domainConfig, redisClient, ssrExchange, t }) =>
        async (context) => {
            const client = createClient({
                publicGraphqlEndpoint: domainConfig.publicGraphqlEndpoint,
                ssrExchange,
                t,
                context,
                redisClient,
            });

            const serverSideProps = await initServerSideProps<GdprConsentsQueryVariablesApi>({
                domainConfig,
                context,
                prefetchedQueries: [
                    {
                        query: GdprConsentsQueryDocumentApi,
                        variables: { action: GdprActionTypeEnumApi.RegisterApi, orderType: null },
                    },
                ],
                client,
                ssrExchange,
            });

            const isLogged = checkIsLoggedOnSSR(client);
            if (isLogged) {
                let redirectUrl = '/';
                if (typeof context.query.r === 'string') {
                    redirectUrl = context.query.r;
                }

                return {
                    redirect: {
                        statusCode: 302,
                        destination: redirectUrl,
                    },
                };
            }

            return serverSideProps;
        },
);

export default Index;
